import { FILTER_GROUPS } from "config/filters";

// Slug -> gateway subPageType + literal searchParams for SDC Near Me Pages.
// SEO copy (title/description/H1/canonical) is supplied by the gateway, not here.
// Amenity slugs send the singular `amenity` CLP slug so the gateway's amenityMap
// (selfstorage_api_gateway SearchClient.js) interpolates it into the amenities
// filter — mirrors ssdr. Size slugs send a direct `sqft` range (no conversion).
// breadcrumbLabel is the static two-level breadcrumb text: Home → [breadcrumbLabel].
// It must match the visible H1 on the page and is never fetched from the API.
export const nearMeKeywords = {
	// amenity slugs
	"24-hour-storage-units": {
		subPageType: "24-hour",
		searchParams: { amenity: "24-hour-access" },
		breadcrumbLabel: "24 Hour Storage Units Near Me"
	},
	"climate-controlled-storage-units": {
		subPageType: "climate-controlled",
		searchParams: { amenity: "climate-controlled" },
		breadcrumbLabel: "Climate Controlled Storage Units Near Me"
	},
	"drive-up-storage-units": {
		subPageType: "drive-up",
		searchParams: { amenity: "drive-up-self-storage" },
		breadcrumbLabel: "Drive Up Storage Units Near Me"
	},
	"storage-units-with-free-truck-rental": {
		subPageType: "free-truck-rental",
		searchParams: { amenity: "free-truck-rental" },
		breadcrumbLabel: "Storage Units With Free Truck Rental Near Me"
	},
	// size slugs
	"5x5-storage-units": {
		subPageType: "5x5",
		searchParams: { sqft: "0-25" },
		breadcrumbLabel: "5x5 Storage Units Near Me"
	},
	"5x10-storage-units": {
		subPageType: "5x10",
		searchParams: { sqft: "26-60" },
		breadcrumbLabel: "5x10 Storage Units Near Me"
	},
	"5x15-storage-units": {
		subPageType: "5x15",
		searchParams: { sqft: "61-75" },
		breadcrumbLabel: "5x15 Storage Units Near Me"
	},
	"10x10-storage-units": {
		subPageType: "10x10",
		searchParams: { sqft: "76-100" },
		breadcrumbLabel: "10x10 Storage Units Near Me"
	},
	"10x15-storage-units": {
		subPageType: "10x15",
		searchParams: { sqft: "101-150" },
		breadcrumbLabel: "10x15 Storage Units Near Me"
	},
	"10x20-storage-units": {
		subPageType: "10x20",
		searchParams: { sqft: "151-200" },
		breadcrumbLabel: "10x20 Storage Units Near Me"
	},
	"10x25-storage-units": {
		subPageType: "10x25",
		searchParams: { sqft: "201-250" },
		breadcrumbLabel: "10x25 Storage Units Near Me"
	},
	"10x30-storage-units": {
		subPageType: "10x30",
		searchParams: { sqft: "251-300" },
		breadcrumbLabel: "10x30 Storage Units Near Me"
	},
	// deals
	deals: {
		subPageType: "deals",
		searchParams: {},
		breadcrumbLabel: "Storage Unit Deals Near Me"
	}
};

// Maps subPageType → breadcrumbLabel for use in components that receive
// subPageType from Redux (not the URL slug directly).
const subPageTypeMap = Object.values(nearMeKeywords).reduce(
	(acc, { subPageType, breadcrumbLabel, searchParams }) => {
		acc[subPageType] = { breadcrumbLabel, searchParams };
		return acc;
	},
	{}
);

export function getKeywordConfig(slug) {
	return nearMeKeywords[slug] || null;
}

// Returns the static breadcrumb label for an NMP subPageType, or null if unknown.
export function getNMPBreadcrumbLabel(subPageType) {
	return subPageTypeMap[subPageType]?.breadcrumbLabel || null;
}

// Returns the base searchParams that define an NMP (e.g. { amenity: "climate-controlled" }
// or { sqft: "0-25" }) for a given subPageType, or null if unknown.
export function getNMPSearchParams(subPageType) {
	return subPageTypeMap[subPageType]?.searchParams || null;
}

// Amenity CLP slug (searchParams.amenity) -> amenities filter option value, so
// the amenity NMP base can be matched against the checkbox options in Redux.
const amenitySlugToFilterValue = {
	"24-hour-access": "twentyFourHourAccess",
	"climate-controlled": "climateControlled",
	"drive-up-self-storage": "driveUp",
	"free-truck-rental": "freeTruckRental"
};

// Returns the NMP's base filter(s) as { name, value } entries keyed to the
// searchFilters groups (e.g. [{ name: "sqft", value: "0-25" }]). Used to detect
// when the user has cleared the filter that defines the page (e.g. unchecked
// "5x5" on the 5x5 Near Me page), so Apply Filters can send them to the city's
// landing page instead. Returns [] for non-NMP pages and the deals NMP (no
// defining filter).
export function getNMPBaseFilters(subPageType) {
	const params = getNMPSearchParams(subPageType);
	if (!params) return [];

	const baseFilters = [];
	if (params.sqft) {
		baseFilters.push({ name: FILTER_GROUPS.UNIT_SIZE, value: params.sqft });
	}
	if (params.amenity) {
		const value = amenitySlugToFilterValue[params.amenity];
		if (value) {
			baseFilters.push({ name: FILTER_GROUPS.AMENITIES, value });
		}
	}
	return baseFilters;
}
